import { HomeIcon, UserIcon, BriefcaseIcon, MailIcon, ChevronLeftIcon } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import LanguageSelector from './LanguageSelector';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Ícones Font Awesome - versão com debug
const Home = () => {
  return <HomeIcon />;
};

const AboutIcon = () => {
 return <UserIcon />;
};

const ServicesIcon = () => {
  return <BriefcaseIcon />;
};

const ContactIcon = () => {
  return <MailIcon />;
};

const ChevronLeft = () => (
  <ChevronLeftIcon />
);

const Header = () => {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

 

  // Detectar se é mobile
  useEffect(() => {
    const checkIsMobile = () => {
      const mobileCheck = window.innerWidth <= 1024;
      setIsMobile(mobileCheck);

      if (mobileCheck) { // Se for mobile
        setIsCollapsed(false); // <--- Adicionar esta linha para forçar o menu a expandir
      }
      
      if (window.innerWidth > 1024) {
        setIsMobileMenuOpen(false);
      }
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
}, []);

  // Detectar seção ativa baseada no scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['hero', 'about', 'services', 'contact'];
      const scrollPosition = window.scrollY + 100;

      if (window.scrollY < 50) {
        setActiveSection('hero');
        return;
      }

      let foundActiveSection = false;
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(section);
            foundActiveSection = true;
            break;
          }
        }
      }

      if (!foundActiveSection && window.scrollY < 200) {
        setActiveSection('hero');
      }
    };

    handleScroll();

    if (location.pathname === '/' && window.scrollY < 200) {
      setActiveSection('hero');
    }

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (location.pathname === '/' && window.scrollY < 200) {
      setActiveSection('hero');
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen, isMobile]);

  useEffect(() => {
    const mainContent =
      document.querySelector('.main-content') || document.body;

    if (!isMobile) {
      if (isCollapsed) {
        mainContent.classList.add('collapsed');
      } else {
        mainContent.classList.remove('collapsed');
      }
    } else {
      mainContent.classList.remove('collapsed');
    }
  }, [isCollapsed, isMobile]);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const scrollToSection = sectionId => {
    setActiveSection(sectionId);

    if (location.pathname !== '/') {
      navigate('/', { replace: true });
      setTimeout(() => {
        if (sectionId === 'hero') {
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        } else {
          const element = document.getElementById(sectionId);
          if (element) {
            const headerHeight = 80;
            const elementPosition = element.offsetTop - headerHeight;

            window.scrollTo({
              top: elementPosition,
              behavior: 'smooth',
            });
          }
        }
      }, 100);
    } else {
      if (sectionId === 'hero') {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      } else {
        const element = document.getElementById(sectionId);
        if (element) {
          const headerHeight = 80;
          const elementPosition = element.offsetTop - headerHeight;

          window.scrollTo({
            top: elementPosition,
            behavior: 'smooth',
          });
        }
      }
    }

    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  };

  const menuItems = [
    { id: 'hero', label: t('nav_home'), icon: <HomeIcon /> },
    { id: 'about', label: t('nav_about'), icon: <AboutIcon /> },
    { id: 'services', label: t('nav_services'), icon: <ServicesIcon /> },
    { id: 'contact', label: t('nav_contact'), icon: <ContactIcon /> },
  ];

  return (
    <>
      {/* Botão do menu mobile */}
      {isMobile && (
        <button
          className={`mobile-menu ${isMobileMenuOpen ? 'active' : ''}`}
          onClick={toggleMobileMenu}
          aria-label={isMobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
          aria-expanded={isMobileMenuOpen}
        >
          <div className='hamburger'>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </button>
      )}

      {/* Overlay para mobile */}
      {isMobile && (
        <div
          className={`sidebar-overlay ${isMobileMenuOpen ? 'active' : ''}`}
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <header
        className={`header ${isCollapsed ? 'collapsed' : ''} ${
          isMobile && isMobileMenuOpen ? 'mobile-open' : ''
        }`}
      >
        <div className='header-container'>
          <div className='header-top'>
            <a
              href='#hero'
              className='logo'
              onClick={e => {
                e.preventDefault();
                scrollToSection('hero');
              }}
            >
              <img
                src='/Logomarca.png'
                alt='PC Perícias e Consultorias'
                className='logo-image-header'
              />
              {!isCollapsed && <span className='logo-text'></span>}
            </a>

            {!isMobile && (
              <button
                className='collapse-btn'
                onClick={toggleCollapse}
                aria-label={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
              >
                <ChevronLeft />
              </button>
            )}
          </div>

          <nav className='nav'>
            <ul className='nav'>
              {menuItems.map(item => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className={activeSection === item.id ? 'active' : ''}
                    onClick={e => {
                      e.preventDefault();
                      scrollToSection(item.id);
                    }}
                  >
                    {item.icon} 
                    
                    {(!isCollapsed || isMobileMenuOpen) && ( 
                      <span className='nav-text'>{item.label || item.id}</span>
                    )}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Header Actions */}
          <div className='header-actions'>
            <LanguageSelector isCollapsed={isCollapsed} />
            <ThemeToggle />
            
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;